/**
 * The Grid on one 2D canvas, in greyscale but for two colours (look.ts): the
 * Walker's red, for the Walker and everything it does, and the hands' cyan, for
 * every Visitor's cursor and the Cells they have just painted.
 *
 * No ground of its own: the canvas is clear, so the Grid sits in the page's
 * space with no box round it, and a margin of open space lets hits and the
 * Walker spill past its edges. It is a dot Grid with no lattice lines. Ink means sound: a kick is a
 * disc, a snare a ring, a hat a small dot, a tom a diamond, all in the one
 * white. A Wall is its Cell outlined in it, so a run of Walls reads as a built
 * line. The glyphs are
 * one Path2D per Cell value, rebuilt only when the Grid changes.
 *
 * The Walker is a character, not a marker: a chunky red body with one eye,
 * and a limb reaching toward each neighbour it can enter, as long as that
 * neighbour's Odds. Its instinct is its body: while it wanders the limbs are
 * even, while it aims they lean down the route, and the eye looks where the
 * Odds point. It slides into each Cell, squashes as it lands, and sleeps with
 * its eye shut while nobody is here. The last three Bars of its walk trail behind it
 * as a tapering line, the Events it struck marked red along the way.
 *
 * Every sounded hit punches, its glyph knocked out of the flooded Cell, and its
 * Forms each show as themselves: strike.ts.
 *
 * Other hands draw over the glyphs and under the Walker: a cursor is four cyan
 * corner brackets round its Cell, and this page's own also shows the chosen
 * Brush's glyph there. A freshly painted Cell flashes cyan, glyph and all, and
 * fades back to ink. Decides nothing about the world; it draws what it is
 * handed, every frame.
 */

import { CELL_PX, GRID_PX, GROUND, HANDS, MARGIN_PX, WALKER } from "./look.ts"
import type { Odds } from "./path.ts"
import { glowOn, TRAIL_STEPS } from "./reach.ts"
import type { Action } from "./world.ts"
import type { Strike } from "./strike.ts"
import { strikeOf } from "./strike.ts"
import { cellValue, EMPTY, GRID_CELLS, GRID_SIDE, HAT, KICK, SNARE, TOM, WALL } from "./world.ts"

export { CELL_PX, GRID_PX, MARGIN_PX }

const POINT = "#2e2e2e"
const INK = "#dcdcdc"

const POINT_R = 1.4
const KICK_R = CELL_PX * 0.34
const SNARE_R = CELL_PX * 0.27
const HAT_R = CELL_PX * 0.12
/** Centre to corner: a diamond reads about as heavy as the snare's ring. */
const TOM_R = CELL_PX * 0.32
/** A Wall is its Cell outlined, a hair inside the edge, so a run of them reads as a built line. */
const WALL_INSET = 3
const WALL_LINE = 1.5

/** A paint flash's life: cyan at once, back to ink by the end. */
export const FLASH_MS = 900

/** The Walker's slide into a Cell, and its squash as it lands. */
const SLIDE_MS = 55
const SQUASH_MS = 140
const BODY = CELL_PX * 0.96
const BODY_CORNER = CELL_PX * 0.3
const LIMB_W = CELL_PX * 0.4
/** A limb at Odds 1 reaches the neighbour's centre; at the floor, barely past the body. */
const LIMB_MIN = CELL_PX * 0.1
const LIMB_MAX = CELL_PX - BODY / 2
/** How fast limbs follow new Odds: a time constant, ms. */
const LIMB_EASE_MS = 45

export interface Hit {
  readonly cell: number
  /** The page time it sounded. */
  readonly at: number
  readonly forms?: ReadonlyArray<string> | undefined
}

/** What the Walker is up to, as far as its face goes. */
export type Mood = "awake" | "standing" | "resting" | "off"

export interface Scene {
  /** The Grid as the page draws it: the server's, with the optimistic overlay over it. */
  readonly grid: string
  readonly walker: number | undefined
  /** The latest sounded hit; a new object for each. */
  readonly hit: Hit | undefined
  /** The Odds of the Step after the one the Walker stands on: the reach of its limbs. */
  readonly odds: Odds | undefined
  /** The Cells the Walker has left, newest last. */
  readonly trail: ReadonlyArray<number>
  /** The Cell under every other Visitor's pointer. */
  readonly others: ReadonlyArray<number>
  /** The Cell under this page's own pointer, -1 for none, and the Brush value it would paint. */
  readonly pointer: number
  readonly brush: number
  /** Freshly painted Cells, each with the page time it was painted. */
  readonly flashes: ReadonlyMap<number, number>
  /** A Step's length at the room's tempo, ms: an Echo's repeats keep time with it. */
  readonly stepMs: number
  readonly mood: Mood
}

export interface GridCanvas {
  readonly element: HTMLCanvasElement
  readonly draw: (scene: Scene, now: number) => void
}

const centre = (cell: number): readonly [number, number] => [
  ((cell % GRID_SIDE) + 0.5) * CELL_PX,
  (Math.floor(cell / GRID_SIDE) + 0.5) * CELL_PX
]

const DIRECTION: Readonly<Record<Action, readonly [number, number]>> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0]
}
const ACTIONS: ReadonlyArray<Action> = ["up", "down", "left", "right"]

/** Without the moveTo each arc would be joined to the last by a line. */
const circle = (path: Path2D | CanvasRenderingContext2D, x: number, y: number, radius: number) => {
  path.moveTo(x + radius, y)
  path.arc(x, y, radius, 0, Math.PI * 2)
}

const diamond = (path: Path2D | CanvasRenderingContext2D, x: number, y: number, radius: number) => {
  path.moveTo(x, y - radius)
  path.lineTo(x + radius, y)
  path.lineTo(x, y + radius)
  path.lineTo(x - radius, y)
  path.closePath()
}

/** One Path2D per Cell value, so the whole Grid is a handful of fills and one stroke. */
interface Glyphs {
  readonly empty: Path2D
  readonly filled: Path2D
  readonly snares: Path2D
  readonly walls: Path2D
}

const glyphsOf = (grid: string): Glyphs => {
  const glyphs: Glyphs = {
    empty: new Path2D(),
    filled: new Path2D(),
    snares: new Path2D(),
    walls: new Path2D()
  }
  for (let cell = 0; cell < GRID_CELLS; cell++) {
    const [x, y] = centre(cell)
    switch (cellValue(grid, cell)) {
      case KICK:
        circle(glyphs.filled, x, y, KICK_R)
        break
      case SNARE:
        circle(glyphs.snares, x, y, SNARE_R)
        break
      case HAT:
        circle(glyphs.filled, x, y, HAT_R)
        break
      case TOM:
        diamond(glyphs.filled, x, y, TOM_R)
        break
      case WALL: {
        const left = x - CELL_PX / 2 + WALL_INSET
        const top = y - CELL_PX / 2 + WALL_INSET
        glyphs.walls.rect(left, top, CELL_PX - WALL_INSET * 2, CELL_PX - WALL_INSET * 2)
        break
      }
      default:
        circle(glyphs.empty, x, y, POINT_R)
    }
  }
  return glyphs
}

/** One glyph drawn alone, in whatever the context's colours are: for flashes, knock-outs and the Brush preview. */
const drawGlyph = (context: CanvasRenderingContext2D, value: number, x: number, y: number) => {
  context.beginPath()
  switch (value) {
    case KICK:
      circle(context, x, y, KICK_R)
      context.fill()
      return
    case SNARE:
      circle(context, x, y, SNARE_R)
      context.lineWidth = 2
      context.stroke()
      return
    case HAT:
      circle(context, x, y, HAT_R)
      context.fill()
      return
    case TOM:
      diamond(context, x, y, TOM_R)
      context.fill()
      return
    case WALL:
      context.rect(
        x - CELL_PX / 2 + WALL_INSET,
        y - CELL_PX / 2 + WALL_INSET,
        CELL_PX - WALL_INSET * 2,
        CELL_PX - WALL_INSET * 2
      )
      context.lineWidth = WALL_LINE
      context.stroke()
      return
    default:
      // The eraser: an empty Cell's point, inside a dashed square.
      circle(context, x, y, POINT_R + 0.4)
      context.fill()
      context.beginPath()
      context.setLineDash([2, 2])
      context.lineWidth = 1
      context.strokeRect(x - CELL_PX * 0.3, y - CELL_PX * 0.3, CELL_PX * 0.6, CELL_PX * 0.6)
      context.setLineDash([])
  }
}

const easeOut = (t: number): number => 1 - (1 - t) * (1 - t) * (1 - t)

interface Struck {
  readonly hit: Hit
  readonly value: number
  readonly strike: Strike
}

export const makeGridCanvas = (element: HTMLCanvasElement): GridCanvas => {
  // The canvas is the Grid and the open space round it; the Grid's own coordinates start inside the margin.
  const side = GRID_PX + MARGIN_PX * 2
  const scale = window.devicePixelRatio || 1
  element.width = side * scale
  element.height = side * scale
  element.style.width = `${side}px`
  element.style.height = `${side}px`
  const context = element.getContext("2d")
  if (context === null) throw new Error("no 2D canvas")
  context.scale(scale, scale)
  context.translate(MARGIN_PX, MARGIN_PX)

  let lastGrid: string | undefined
  let glyphs = glyphsOf("")

  // The Walker's own body state: where it slid from, when it landed, its limbs.
  let at: readonly [number, number] | undefined
  let from: readonly [number, number] = [0, 0]
  let cell: number | undefined
  let movedAt = -Infinity
  let heading: readonly [number, number] = [0, 0]
  const limbs: Record<Action, number> = { up: 0, down: 0, left: 0, right: 0 }
  let lastFrame: number | undefined
  let blinkAt = 0

  let lastHit: Hit | undefined
  let struck: Array<Struck> = []

  const walkerAt = (now: number): readonly [number, number] => {
    const t = Math.min(1, (now - movedAt) / SLIDE_MS)
    const [tx, ty] = centre(cell ?? 0)
    const e = easeOut(t)
    return [from[0] + (tx - from[0]) * e, from[1] + (ty - from[1]) * e]
  }

  const follow = (walker: number | undefined, now: number) => {
    if (walker === cell) return
    if (walker === undefined) {
      cell = undefined
      at = undefined
      return
    }
    const target = centre(walker)
    const current = cell === undefined ? target : walkerAt(now)
    const dx = target[0] - current[0]
    const dy = target[1] - current[1]
    // Across the seam it would slide the whole Grid: it steps out one side and in the other, at once.
    const seam = Math.abs(dx) > CELL_PX * 1.5 || Math.abs(dy) > CELL_PX * 1.5
    from = seam ? target : current
    if (cell !== undefined) {
      const length = Math.hypot(dx, dy) || 1
      heading = seam ? heading : [dx / length, dy / length]
    }
    cell = walker
    movedAt = now
  }

  const strike = (scene: Scene) => {
    if (scene.hit === lastHit) return
    lastHit = scene.hit
    if (scene.hit === undefined) return
    const value = cellValue(scene.grid, scene.hit.cell)
    // A rest sounds nothing, and punches nothing.
    if (value === EMPTY || value === WALL) return
    const strike = strikeOf(value, scene.hit.forms, scene.stepMs, scene.hit.at, scene.hit.cell)
    struck.push({ hit: scene.hit, value, strike })
  }

  const drawTrail = (scene: Scene) => {
    const count = scene.trail.length
    if (count === 0) return
    // Where it struck along the way: a red mark on each Event it left, fading with age.
    context.fillStyle = WALKER
    scene.trail.forEach((trailCell, index) => {
      const value = cellValue(scene.grid, trailCell)
      if (value === EMPTY || value === WALL) return
      const age = (count - index) / (TRAIL_STEPS + 1)
      const [x, y] = centre(trailCell)
      context.globalAlpha = 0.28 * (1 - age)
      context.fillRect(x - CELL_PX / 2 + 1, y - CELL_PX / 2 + 1, CELL_PX - 2, CELL_PX - 2)
    })
    // The line it walked, tapering and fading into the past, up to the body as it slides.
    const points = scene.trail.map(centre)
    if (at !== undefined) points.push(at)
    context.strokeStyle = WALKER
    context.lineCap = "round"
    for (let i = 1; i < points.length; i++) {
      const [x0, y0] = points[i - 1]!
      const [x1, y1] = points[i]!
      if (Math.abs(x1 - x0) > CELL_PX * 1.5 || Math.abs(y1 - y0) > CELL_PX * 1.5) continue
      if (x0 === x1 && y0 === y1) continue
      const fresh = i / points.length
      context.globalAlpha = 0.1 + 0.8 * fresh * fresh
      context.lineWidth = 1 + 4 * fresh
      context.beginPath()
      context.moveTo(x0, y0)
      context.lineTo(x1, y1)
      context.stroke()
    }
    context.globalAlpha = 1
  }

  const drawStruck = (now: number) => {
    struck = struck.filter((one) => now < one.strike.end)
    for (const one of struck) {
      const [x, y] = centre(one.hit.cell)
      if (one.strike.draw(context, x, y, now) > 0.35) {
        context.fillStyle = GROUND
        context.strokeStyle = GROUND
        drawGlyph(context, one.value, x, y)
      }
    }
  }

  const bracket = (x: number, y: number) => {
    const h = CELL_PX / 2 + 1
    const arm = CELL_PX * 0.28
    for (const [sx, sy] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1]
    ] as const) {
      const cx = x + sx * h
      const cy = y + sy * h
      context.moveTo(cx - sx * arm, cy)
      context.lineTo(cx, cy)
      context.lineTo(cx, cy - sy * arm)
    }
  }

  const drawHands = (scene: Scene, now: number) => {
    // Fresh paint: a cyan Cell and a cyan glyph, fading back to ink.
    for (const [flashed, paintedAt] of scene.flashes) {
      const age = (now - paintedAt) / FLASH_MS
      if (age < 0 || age >= 1) continue
      const [x, y] = centre(flashed)
      context.globalAlpha = 0.3 * (1 - age)
      context.fillStyle = HANDS
      context.fillRect(x - CELL_PX / 2, y - CELL_PX / 2, CELL_PX, CELL_PX)
      context.globalAlpha = 1 - age
      context.strokeStyle = HANDS
      drawGlyph(context, cellValue(scene.grid, flashed), x, y)
    }
    context.globalAlpha = 1
    context.strokeStyle = HANDS
    context.lineWidth = 1.5
    context.lineCap = "square"
    context.beginPath()
    for (const other of scene.others) {
      const [x, y] = centre(other)
      bracket(x, y)
    }
    if (scene.pointer >= 0) {
      const [x, y] = centre(scene.pointer)
      bracket(x, y)
    }
    context.stroke()
    if (scene.pointer >= 0) {
      const [x, y] = centre(scene.pointer)
      context.globalAlpha = 0.55
      context.fillStyle = HANDS
      drawGlyph(context, scene.brush, x, y)
      context.globalAlpha = 1
    }
  }

  const drawWalker = (scene: Scene, now: number, dt: number) => {
    if (at === undefined || cell === undefined) return
    const [x, y] = at
    const awake = scene.mood === "awake"

    // The limbs follow the Odds, easing, so a new Step's instinct grows rather than blinks.
    const targets: Record<Action, number> = { up: 0, down: 0, left: 0, right: 0 }
    if (awake && scene.odds !== undefined) {
      for (const glow of glowOn(scene.grid, cell, scene.odds)) {
        targets[glow.action] = LIMB_MIN + (LIMB_MAX - LIMB_MIN) * glow.peak
      }
    }
    const ease = 1 - Math.exp(-dt / LIMB_EASE_MS)
    let gazeX = 0
    let gazeY = 0
    let total = 0
    for (const action of ACTIONS) {
      limbs[action] += (targets[action] - limbs[action]) * ease
      const [dx, dy] = DIRECTION[action]
      const pull = Math.max(0, limbs[action] - LIMB_MIN)
      gazeX += dx * pull
      gazeY += dy * pull
      total += limbs[action]
    }
    // Looking where its Odds lean; with even Odds, the way it last went.
    const lean = Math.hypot(gazeX, gazeY)
    const [lookX, lookY] =
      total > 0 && lean > CELL_PX * 0.05 ? [gazeX / lean, gazeY / lean] : [heading[0] * 0.6, heading[1] * 0.6]

    // Squash on landing, and again on every hit, a Late Step's stutter included.
    const landed = Math.max(movedAt, lastHit?.cell === cell ? lastHit.at : -Infinity)
    const squash = Math.max(0, 1 - (now - landed) / SQUASH_MS)
    const breath = awake ? 0 : Math.sin(now / 700) * 0.035
    const [hx, hy] = heading
    const along = 1 + 0.28 * squash * squash
    const across = 1 - 0.18 * squash * squash
    const sx = (Math.abs(hx) > 0.5 ? along : Math.abs(hy) > 0.5 ? across : 1) + breath
    const sy = (Math.abs(hy) > 0.5 ? along : Math.abs(hx) > 0.5 ? across : 1) - breath

    const colour = scene.mood === "off" ? "#5a5a5a" : WALKER
    context.save()
    context.translate(x, y)
    context.shadowColor = colour
    context.shadowBlur = awake ? 22 : 10
    context.fillStyle = colour
    context.beginPath()
    for (const action of ACTIONS) {
      const reach = limbs[action]
      if (reach < 0.5) continue
      const [dx, dy] = DIRECTION[action]
      const length = BODY / 2 + reach
      // A limb is a rounded bar from the body's middle out toward its neighbour.
      const w = LIMB_W * (0.6 + 0.4 * Math.min(1, reach / LIMB_MAX))
      if (dx !== 0) context.roundRect(dx > 0 ? 0 : -length, -w / 2, length, w, w / 2)
      else context.roundRect(-w / 2, dy > 0 ? 0 : -length, w, length, w / 2)
    }
    // Body and limbs are one shape, filled and glowing as one.
    context.roundRect((-BODY / 2) * sx, (-BODY / 2) * sy, BODY * sx, BODY * sy, BODY_CORNER)
    context.fill()
    context.shadowBlur = 0

    // The eye: tall and dark, blinking now and then; a shut line while it sleeps.
    if (now > blinkAt + 4000 + Math.random() * 6000) blinkAt = now
    const blinking = now - blinkAt < 110
    const ex = lookX * CELL_PX * 0.18
    const ey = lookY * CELL_PX * 0.16
    context.fillStyle = GROUND
    context.beginPath()
    if (!awake || blinking) {
      context.roundRect(ex - CELL_PX * 0.17, ey - 1.2, CELL_PX * 0.34, 2.4, 1.2)
    } else {
      const w = CELL_PX * 0.2
      const h = CELL_PX * 0.34
      context.roundRect(ex - w / 2, ey - h / 2, w, h, w / 2)
    }
    context.fill()
    context.restore()

    // Resting until the budget's midnight: z's rising off it.
    if (scene.mood === "resting") {
      context.strokeStyle = WALKER
      context.lineWidth = 1.5
      context.lineJoin = "round"
      for (let i = 0; i < 3; i++) {
        const t = (((now / 2400 + i / 3) % 1) + 1) % 1
        const size = 3 + t * 4
        const zx = x + CELL_PX * 0.5 + t * CELL_PX * 0.9
        const zy = y - CELL_PX * 0.5 - t * CELL_PX * 1.4
        context.globalAlpha = Math.sin(t * Math.PI)
        context.beginPath()
        context.moveTo(zx - size / 2, zy - size / 2)
        context.lineTo(zx + size / 2, zy - size / 2)
        context.lineTo(zx - size / 2, zy + size / 2)
        context.lineTo(zx + size / 2, zy + size / 2)
        context.stroke()
      }
      context.globalAlpha = 1
    }
  }

  const draw = (scene: Scene, now: number) => {
    const dt = lastFrame === undefined ? 16 : Math.min(100, now - lastFrame)
    lastFrame = now
    if (scene.grid !== lastGrid) {
      lastGrid = scene.grid
      glyphs = glyphsOf(scene.grid)
    }
    follow(scene.walker, now)
    at = cell === undefined ? undefined : walkerAt(now)
    strike(scene)

    // Clear, not filled: the Grid sits in the page's own space, and the ground's flush shows through it.
    context.clearRect(-MARGIN_PX, -MARGIN_PX, side, side)

    // The neighbours it may step to, faintly lit under their glyphs, as far as its limbs say.
    if (cell !== undefined && scene.mood === "awake") {
      context.fillStyle = WALKER
      for (const action of ACTIONS) {
        const reach = limbs[action]
        if (reach < 0.5) continue
        const [dx, dy] = DIRECTION[action]
        const col = ((cell % GRID_SIDE) + dx + GRID_SIDE) % GRID_SIDE
        const row = (Math.floor(cell / GRID_SIDE) + dy + GRID_SIDE) % GRID_SIDE
        context.globalAlpha = 0.22 * (reach / LIMB_MAX)
        context.fillRect(col * CELL_PX, row * CELL_PX, CELL_PX, CELL_PX)
      }
      context.globalAlpha = 1
    }

    drawTrail(scene)

    context.fillStyle = POINT
    context.fill(glyphs.empty)
    context.fillStyle = INK
    context.fill(glyphs.filled)
    context.strokeStyle = INK
    context.lineWidth = 2
    context.stroke(glyphs.snares)
    context.lineWidth = WALL_LINE
    context.stroke(glyphs.walls)

    drawStruck(now)
    drawHands(scene, now)
    drawWalker(scene, now, dt)
  }

  return { element, draw }
}
